import { Resource, ResourceMetadata } from '@esg/esg-global-types';
import { Box, IconButton, Theme, useTheme } from '@mui/material';
import React from 'react';
import { BodyText, CaptionText, MinorHeading } from '../../shared/typography/PrimaryTypography';
import PrimaryDivider from '../../shared/decoration/PrimaryDivider';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/Download.svg';
import { GCP_RESOURCE_UPLOAD_BUCKET } from '../../../env';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { downloadFileFromSignedUrl } from '../../../lib/files/file';
import {
  createResourceMetadata,
  updateResourceMetadata
} from '../../../graphql/services/resource_metadata.service';
import {
  CreateResourceMetadataClientData,
  UpdateResourceMetadataClientData
} from '@esg/syntiro-api-core';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { UserContext } from '../../../context/UserContext';
import { NavigateFunction, useNavigate } from 'react-router-dom';

interface ResourceCardProps {
  resource: Resource;
  resource_metadata: ResourceMetadata | null;
  handleUpdateResourcesMetadata: (updated_resource_metadata: ResourceMetadata) => void;
}

/**
 * Styled card to display Resource Centre Resource data in formatted fashion.
 * @param {Resource} resource Resource data to display in card.
 * @param {ResourceMetadata | null} resource_metadata Company level metadata for master list resource which may or may not exist yet.
 * @param {(updated_resource_metadata: ResourceMetadata): void} handleUpdateResourcesMetadata Update resource metadata for company when downloading resource file.
 * @returns {JSX.Element}
 */
const ResourceCard = ({
  handleUpdateResourcesMetadata,
  resource,
  resource_metadata
}: ResourceCardProps) => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [loadingResourceDownload, setLoadingResourceDownload] = React.useState<boolean>(false);

  const theme: Theme = useTheme();
  const navigate: NavigateFunction = useNavigate();

  const handleResourceDownload = async (): Promise<void> => {
    setLoadingResourceDownload(true);
    try {
      if (group && company && user) {
        await downloadFileFromSignedUrl(GCP_RESOURCE_UPLOAD_BUCKET, resource.uri);
        if (resource_metadata) {
          const update_resource_metadata_client_data: UpdateResourceMetadataClientData = {
            id: resource_metadata.id,
            download_count: resource_metadata.download_count + 1,
            master_list_resource: resource
          };
          const updated_resource_metadata: ResourceMetadata | null | undefined =
            await updateResourceMetadata(
              { group_id: group.id, company_id: company.id },
              update_resource_metadata_client_data
            );
          if (updated_resource_metadata) handleUpdateResourcesMetadata(updated_resource_metadata);
        } else {
          const create_resource_metadata_client_data: CreateResourceMetadataClientData = {
            download_count: 1,
            master_list_resource: resource
          };
          const created_resource_metadata: ResourceMetadata | null | undefined =
            await createResourceMetadata(
              { group_id: group.id, company_id: company.id },
              create_resource_metadata_client_data
            );
          if (created_resource_metadata) handleUpdateResourcesMetadata(created_resource_metadata);
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ResourceCard failed on an unknown error while calling handleResourceDownload.',
          {
            resource: resource
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to download resource. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setLoadingResourceDownload(false);
    }
  };

  // Calc to show size in B, KB, MB or GB
  const getFileSizeDisplay = (bytes: string): string => {
    const num_bytes = Number(bytes);
    if (isNaN(num_bytes)) return '';
    const size_units: Array<string> = ['B', 'KB', 'MB', 'GB'];

    let display_value: number = num_bytes;
    let unit_index = 0;
    while (display_value > 1024) {
      display_value /= 1024;
      unit_index++;
    }
    return `${display_value.toFixed(2).replace(/\.?0+$/, '')} ${size_units[unit_index]}`;
  };

  // Display message for resource download count rounded to nearest 1000 (floor).
  const getDownloadsDisplay = (downloads: number): string => {
    if (downloads < 1000) return downloads.toString();
    return `${Math.floor(downloads / 1000) * 1000}+`;
  };

  // Nav to details view for resource
  const handleDetailsNav = (): void => {
    navigate('/resources/details', {
      state: {
        resource: resource,
        resource_metadata: resource_metadata
      }
    });
  };

  return (
    <Box
      sx={{
        height: '100%',
        border: `1px solid ${theme.palette.teal.dark}`,
        borderRadius: '12px',
        minHeight: 300,
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left'
      }}
    >
      <Box
        onClick={handleDetailsNav}
        sx={{
          cursor: 'pointer'
        }}
      >
        <img
          src={`https://storage.googleapis.com/${GCP_RESOURCE_UPLOAD_BUCKET}/${resource.thumbnail_uri}`}
          alt="RESOURCE"
          style={{
            maxWidth: '100%',
            borderRadius: '12px 12px 0 0',
            height: 250,
            objectFit: 'cover',
            objectPosition: 'center'
          }}
          draggable={false}
        />
        <MinorHeading
          weight="Bold"
          sx={{
            px: 2,
            pt: 2,
            wordBreak: 'break-word',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          {resource.name}
        </MinorHeading>
      </Box>
      <Box sx={{ p: 2 }}>
        <PrimaryDivider opacity={0.7} sx={{ mb: 1 }} />
        <CaptionText weight="Medium">{resource.description}</CaptionText>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            my: 1,
            color: theme.palette.primary.main
          }}
        >
          {resource.allow_download !== false && (
            <IconButton
              loading={loadingResourceDownload}
              onClick={async () => await handleResourceDownload()}
              sx={{ color: 'inherit' }}
              size="large"
            >
              <DownloadIcon height={30} stroke={theme.palette.primary.main} />
            </IconButton>
          )}
          <BodyText sx={{ color: theme.palette.primary.main }}>{resource.type}</BodyText>
          <BodyText sx={{ color: theme.palette.primary.main }}>
            {getFileSizeDisplay(resource.size)}
          </BodyText>
        </Box>
        {resource.allow_download !== false && (
          <CaptionText weight="Medium">
            {`${resource_metadata ? getDownloadsDisplay(resource_metadata.download_count) : 0} Downloads`}
          </CaptionText>
        )}
      </Box>
    </Box>
  );
};

export default ResourceCard;
